<template>
  <div class="corr-section">
    <div class="d-flex align-center mb-1">
      <MessagesSquare class="section-icon mr-3" :size="22" />
      <h2 class="section-title">{{ $gt('Korrespondenz') }}</h2>
    </div>
    <p class="section-subtitle mb-4">
      {{ $gt('Legen Sie fest, wie E-Mails und Nachrichten in der Korrespondenz gruppiert werden – in der Akte und in der zentralen Korrespondenz.') }}
    </p>

    <div class="corr-options" role="radiogroup" :aria-label="$gt('Gruppierung der Korrespondenz')">
      <button
        v-for="option in options"
        :key="option.value"
        type="button"
        role="radio"
        class="corr-option"
        :class="{ 'corr-option--active': current === option.value }"
        :aria-checked="current === option.value ? 'true' : 'false'"
        :disabled="saving"
        data-testid="correspondence-grouping-option"
        @click="choose(option.value)"
      >
        <component :is="option.value === 'group' ? Users : User" class="corr-option__icon" :size="20" />
        <span class="corr-option__text">
          <span class="corr-option__title">
            {{ option.title }}<template v-if="option.recommended"> ({{ $gt('empfohlen') }})</template>
          </span>
          <span class="corr-option__desc">{{ option.description }}</span>
          <span v-if="option.drawback" class="corr-option__drawback" data-testid="correspondence-grouping-drawback">
            <TriangleAlert aria-hidden="true" class="corr-option__drawback-icon" :size="14" />{{ option.drawback }}
          </span>
        </span>
        <Check v-if="current === option.value" :size="18" class="corr-option__check" />
      </button>
    </div>

    <!-- Kundenfeedback 18.10.2026, IV: Umschalten auch direkt in der Korrespondenz. -->
    <p class="corr-quick" data-testid="correspondence-grouping-quick-hint">
      <ArrowLeftRight aria-hidden="true" class="corr-quick__icon" :size="15" />
      <span>{{ $gt('Tipp: Direkt in der Korrespondenz (in der Akte und zentral) können Sie über „Ansicht: Einzeln | Gruppen“ jederzeit umschalten. Die Auswahl dort ändert auch diese Einstellung.') }}</span>
    </p>
  </div>
</template>

<script setup lang="ts">
  // Anforderung 01.10.2026, 2.1: Gruppierung der Korrespondenz je Nutzer.
  import { ArrowLeftRight, Check, MessagesSquare, TriangleAlert, User, Users } from 'lucide-vue-next'
  import { computed, onMounted, ref } from 'vue'
  import { groupingOptions } from '@/components/messages/chat/correspondenceGroupingTexts'
  import { gt } from '@/i18n/gt'
  import { type CorrespondenceGrouping, loadUserSettings, saveUserSetting, useUserSettings } from '@/services/userSettings'
  import { useSnackbarStore } from '@/stores/snackbarStore'

  const settings = useUserSettings()
  const saving = ref(false)
  const current = computed(() => settings.settings.correspondenceGrouping)

  // Gemeinsame Texte mit dem Umschalter in der Korrespondenz.
  const options = groupingOptions()

  onMounted(() => { loadUserSettings() })

  async function choose (value: CorrespondenceGrouping) {
    if (value === current.value) return
    saving.value = true
    try {
      await saveUserSetting('correspondenceGrouping', value)
      useSnackbarStore().showSuccess(gt('Einstellung gespeichert'))
    } catch (error) {
      useSnackbarStore().showError((error as Error).message)
    } finally {
      saving.value = false
    }
  }
</script>

<style scoped>
.section-icon { color: var(--primary-color); }
.section-title { margin: 0; font-size: 1.05rem; font-weight: 700; color: var(--dark-color); }
.section-subtitle { font-size: 0.86rem; color: var(--grey-color); }
.corr-options { display: grid; gap: 10px; }
.corr-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  border: 1px solid var(--border-grey-color);
  border-radius: 12px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.corr-option:hover:not(:disabled) { border-color: var(--primary-color); }
.corr-option:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 2px; }
.corr-option--active { border-color: var(--primary-color); background: var(--primary-light-color); }
.corr-option__icon { flex-shrink: 0; margin-top: 2px; color: var(--primary-color); }
.corr-option__text { display: flex; flex: 1; flex-direction: column; gap: 3px; }
.corr-option__title { font-size: 0.9rem; font-weight: 600; color: var(--dark-color); }
.corr-option__desc { font-size: 0.8rem; line-height: 1.45; color: var(--grey-color); }
.corr-option__check { flex-shrink: 0; color: var(--primary-color); }
.corr-option__drawback { display: flex; gap: 6px; margin-top: 4px; font-size: 0.8rem; line-height: 1.45; color: #9a5b00; }
.corr-option__drawback svg { flex: none; margin-top: 2px; }
.corr-quick {
  display: flex;
  gap: 8px;
  margin: 14px 0 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--light-grey-color, #f4f7fb);
  font-size: 0.8rem;
  line-height: 1.45;
  color: var(--grey-color);
}
.corr-quick__icon { flex: none; margin-top: 2px; color: var(--primary-color); }
</style>
